import { CAR_BY_ID } from '../../shared/cars.js';
import { ARCADE_PRESETS, normaliseArcadePreset } from '../../shared/arcade-presets.js';
import {
  lapsConfigurable,
  MAX_CUSTOM_LAPS,
  MAX_LOBBY_NPCS,
  MAX_LOBBY_PLAYERS,
  MIN_CUSTOM_LAPS,
  MIN_LOBBY_PLAYERS,
  minimumCompetitorsFor,
  npcRosterOf,
  NPC_DIFFICULTY_ORDER,
  type LobbySettingsPatch,
} from '../../shared/lobby.js';
import { eventModeLabel, raceEventMode, raceLapCount } from '../../shared/race-events.js';
import type {
  MapDefinition,
  NpcDifficulty,
  RaceEventMode,
  RaceSettings,
  RivalStyle,
  TimeOfDay,
  VisualTuning,
  Weather,
} from '../../shared/types.js';
import { WEATHER_VALUES } from '../../shared/types.js';
import { drawMapOutline, mapBriefing } from '../map-outline.js';
import { escapeHtml, safeColour } from '../ui/html.js';
import { signalBarsHtml } from '../ui/connection-quality.js';
import { t, tc, tn } from '../i18n/index.js';

export interface LobbyMember {
  id: string;
  name: string;
  carId: string;
  tuning?: VisualTuning;
  host: boolean;
  ready: boolean;
  connected: boolean;
  /** Display-rounded round trip in ms, online only; null until measured. */
  ping?: number | null;
}

export interface LobbyRival {
  /** Grid slot; also the index into the rival roster. */
  index: number;
  name: string;
  carId: string | null;
  paint: string | null;
  style: RivalStyle | null;
  difficulty: NpcDifficulty;
  featured: boolean;
}

export interface LobbyViewModel {
  kind: 'online' | 'offline';
  isHost: boolean;
  localPlayerId: string;
  settings: RaceSettings;
  /** Catalogue version of the selected track (authored atmosphere). */
  map: MapDefinition;
  maps: readonly MapDefinition[];
  members: readonly LobbyMember[];
  rivals: readonly LobbyRival[];
  inviteLink: string | null;
  /** The 3D track behind an online lobby has been built. */
  trackReady: boolean;
  localReady: boolean;
  /** Curated event the lobby was opened for (tour, daily, nemesis …). */
  eventBanner: { kicker: string; title: string } | null;
  /** Busy state while an offline grid is being launched. */
  launching: boolean;
  notice: string;
}

export interface LobbyPanelHandlers {
  patch(patch: LobbySettingsPatch): void;
  setReady(ready: boolean): void;
  forceStart(): void;
  leave(): void;
  openGarage(): void;
  share(): void;
  copyInvite(): void;
  kick(playerId: string, ban: boolean): void;
}

const EVENT_FORMATS: readonly { mode: RaceEventMode; detail: string }[] = [
  { mode: 'circuit', detail: 'First across the line after every lap wins.' },
  { mode: 'sprint', detail: 'One flat-out lap. No second chances.' },
  { mode: 'time-trial', detail: 'Solo against the clock, a ghost and medal targets.' },
  { mode: 'style-rush', detail: 'Drifts, drafts and near misses score. Highest style wins.' },
  { mode: 'elimination', detail: 'Last place drops out at the end of every lap.' },
  { mode: 'heat-run', detail: 'Interceptors escalate each lap. Bank your heat or push on.' },
  { mode: 'road-rage', detail: 'Take down the marked rival. Contact is always on.' },
];

const TIMES: readonly TimeOfDay[] = ['dawn', 'day', 'sunset', 'night'];
const DIFFICULTY_LABEL: Record<NpcDifficulty, string> = { rookie: 'ROOKIE', sport: 'SPORT', pro: 'PRO' };
const svgIcon = (body: string): string => `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;
const SUN_RAYS = 'M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6';
const WEATHER_ICON: Record<Weather, string> = {
  clear: svgIcon(`<circle cx="12" cy="12" r="4.2"/><path d="${SUN_RAYS}"/>`),
  haze: svgIcon('<path d="M3 8.5h13M6 12.5h15M3 16.5h11M17 16.5h3"/>'),
  rain: svgIcon('<path d="M7 15.5a4.5 4.5 0 1 1 1.2-8.8A5.5 5.5 0 0 1 18.6 9 3.3 3.3 0 0 1 18 15.5Z"/><path d="M9 18.5l-1 2.5M13 18.5l-1 2.5M17 18.5l-1 2.5"/>'),
  sand: svgIcon('<path d="M3 9c3-2 5 2 8 0s5-2 8 0M3 13.5c3-2 5 2 8 0s5-2 8 0M5 18c2.5-1.5 4.5 1.5 7 0"/><circle cx="18.5" cy="18" r="0.6"/>'),
  snow: svgIcon('<path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9M9.5 4.5 12 7l2.5-2.5M9.5 19.5 12 17l2.5 2.5"/>'),
};
const TIME_ICON: Record<TimeOfDay, string> = {
  dawn: svgIcon('<path d="M6.5 16a5.5 5.5 0 0 1 11 0"/><path d="M2.5 19.5h19M12 4.5v3M4.8 9l1.8 1.5M19.2 9l-1.8 1.5M9.5 7.2 12 4.5l2.5 2.7"/>'),
  day: svgIcon(`<circle cx="12" cy="12" r="4.2"/><path d="${SUN_RAYS}"/>`),
  sunset: svgIcon('<path d="M6.5 16a5.5 5.5 0 0 1 11 0"/><path d="M2.5 19.5h19M12 4.5v3M4.8 9l1.8 1.5M19.2 9l-1.8 1.5M9.5 4.8 12 7.5l2.5-2.7"/>'),
  night: svgIcon('<path d="M19.5 14.5A8 8 0 0 1 9.5 4.5a8 8 0 1 0 10 10Z"/><path d="M17 3.5v3M15.5 5h3"/>'),
};

/**
 * One lobby view for online rooms and offline sessions.
 *
 * Every member sees the same live settings; only the host gets interactive
 * controls. The panel re-renders from a pure view model and restores focus by
 * `data-focus-key`, so remote edits never yank a guest's keyboard or pad focus.
 */
export class LobbyPanel {
  private model: LobbyViewModel | null = null;
  private signature = '';
  private trackPickerOpen = false;
  private pendingConfirm: { key: string; until: number } | null = null;
  /** Outlines drawn while the overlay is hidden have no size; redraw on resize. */
  private outlineObserver: ResizeObserver | null = null;
  private confirmTimer: number | undefined;
  private readonly onClick = (event: Event): void => this.handleClick(event);
  private readonly onChange = (event: Event): void => this.handleChange(event);
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.trackPickerOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.setTrackPicker(false);
    }
  };

  constructor(
    private readonly container: HTMLElement,
    private readonly handlers: LobbyPanelHandlers,
  ) {
    container.addEventListener('click', this.onClick);
    container.addEventListener('change', this.onChange);
    container.addEventListener('keydown', this.onKey);
  }

  get trackPickerVisible(): boolean {
    return this.trackPickerOpen;
  }

  update(model: LobbyViewModel): void {
    this.model = model;
    const signature = JSON.stringify([
      model.kind, model.isHost, model.localPlayerId, model.settings, model.map.id, model.maps.length,
      model.members, model.rivals, model.inviteLink, model.trackReady, model.localReady,
      model.eventBanner, model.launching, model.notice, this.trackPickerOpen,
      this.pendingConfirm?.key ?? '',
    ]);
    if (signature === this.signature) return;
    this.signature = signature;
    this.render();
  }

  destroy(): void {
    this.container.removeEventListener('click', this.onClick);
    this.container.removeEventListener('change', this.onChange);
    this.container.removeEventListener('keydown', this.onKey);
    if (this.confirmTimer !== undefined) window.clearTimeout(this.confirmTimer);
    this.outlineObserver?.disconnect();
    this.outlineObserver = null;
    this.container.replaceChildren();
    this.model = null;
  }

  private render(): void {
    const model = this.model;
    if (!model) return;
    const active = document.activeElement as HTMLElement | null;
    const focusKey = active && this.container.contains(active) ? active.dataset.focusKey : undefined;
    const scrollers = [...this.container.querySelectorAll<HTMLElement>('[data-scroll-key]')]
      .map((element) => [element.dataset.scrollKey!, element.scrollTop] as const);
    this.container.innerHTML = lobbyHtml(model, this.trackPickerOpen, this.pendingConfirm?.key ?? '');
    for (const [key, top] of scrollers) {
      const element = this.container.querySelector<HTMLElement>(`[data-scroll-key="${key}"]`);
      if (element) element.scrollTop = top;
    }
    this.outlineObserver?.disconnect();
    const draw = (canvas: HTMLCanvasElement): void => {
      const map = model.maps.find((candidate) => candidate.id === canvas.dataset.mapOutline);
      if (map && canvas.clientWidth > 0) drawMapOutline(canvas, map, { sections: canvas.dataset.outlineSections === 'true' });
    };
    const canvases = [...this.container.querySelectorAll<HTMLCanvasElement>('canvas[data-map-outline]')];
    canvases.forEach(draw);
    if (typeof ResizeObserver !== 'undefined') {
      this.outlineObserver = new ResizeObserver((entries) => {
        for (const entry of entries) draw(entry.target as HTMLCanvasElement);
      });
      for (const canvas of canvases) this.outlineObserver.observe(canvas);
    }
    if (focusKey) {
      const next = this.container.querySelector<HTMLElement>(`[data-focus-key="${CSS.escape(focusKey)}"]`);
      if (next && !(next as HTMLButtonElement).disabled) next.focus({ preventScroll: true });
    } else if (this.trackPickerOpen) {
      this.container.querySelector<HTMLElement>('.lobby-track-option[aria-pressed="true"]')?.focus({ preventScroll: true });
    }
  }

  private setTrackPicker(open: boolean): void {
    this.trackPickerOpen = open;
    this.signature = '';
    if (this.model) this.update(this.model);
    if (!open) this.container.querySelector<HTMLElement>('[data-focus-key="open-tracks"]')?.focus({ preventScroll: true });
  }

  private confirm(key: string, action: () => void): void {
    const now = Date.now();
    if (this.pendingConfirm?.key === key && now < this.pendingConfirm.until) {
      this.pendingConfirm = null;
      action();
    } else {
      this.pendingConfirm = { key, until: now + 3_200 };
      if (this.confirmTimer !== undefined) window.clearTimeout(this.confirmTimer);
      this.confirmTimer = window.setTimeout(() => {
        this.pendingConfirm = null;
        this.signature = '';
        if (this.model) this.update(this.model);
      }, 3_200);
    }
    this.signature = '';
    if (this.model) this.update(this.model);
  }

  private handleClick(event: Event): void {
    const model = this.model;
    if (!model) return;
    const field = event.target as HTMLElement;
    if (field instanceof HTMLInputElement && field.readOnly) {
      field.select();
      return;
    }
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-lobby-action],[data-lobby-set],[data-rival-difficulty]');
    if (!target || (target as HTMLButtonElement).disabled) return;
    if (target.dataset.rivalDifficulty !== undefined) {
      if (!model.isHost) return;
      const roster = npcRosterOf(model.settings);
      const index = Number(target.dataset.index);
      const difficulty = target.dataset.rivalDifficulty as NpcDifficulty;
      if (!Number.isInteger(index) || index < 0 || index >= roster.length || roster[index] === difficulty) return;
      roster[index] = difficulty;
      this.handlers.patch({ npcRoster: roster });
      return;
    }
    const setting = target.dataset.lobbySet;
    if (setting) {
      if (!model.isHost) return;
      this.applySetting(setting, target.dataset.value ?? '');
      return;
    }
    const roster = npcRosterOf(model.settings);
    switch (target.dataset.lobbyAction) {
      case 'ready':
        this.handlers.setReady(!model.localReady);
        break;
      case 'force-start':
        this.handlers.forceStart();
        break;
      case 'leave':
        this.handlers.leave();
        break;
      case 'garage':
        this.handlers.openGarage();
        break;
      case 'share':
        this.handlers.share();
        break;
      case 'copy-invite':
        this.handlers.copyInvite();
        break;
      case 'visibility':
        if (model.isHost) this.handlers.patch({ visibility: model.settings.visibility === 'public' ? 'private' : 'public' });
        break;
      case 'add-rival': {
        if (!model.isHost || roster.length >= MAX_LOBBY_NPCS) return;
        const difficulty = (target.dataset.value as NpcDifficulty | undefined) ?? roster.at(-1) ?? 'sport';
        this.handlers.patch({ npcRoster: [...roster, difficulty] });
        break;
      }
      case 'fill-rivals': {
        if (!model.isHost) return;
        const difficulty = roster.at(-1) ?? 'sport';
        const capacity = model.kind === 'online'
          ? Math.min(MAX_LOBBY_NPCS, Math.max(0, 12 - model.members.length))
          : MAX_LOBBY_NPCS;
        if (roster.length >= capacity) return;
        this.handlers.patch({ npcRoster: [...roster, ...Array.from({ length: capacity - roster.length }, () => difficulty)] });
        break;
      }
      case 'remove-rival': {
        if (!model.isHost) return;
        const index = Number(target.dataset.index);
        if (!Number.isInteger(index) || index < 0 || index >= roster.length) return;
        roster.splice(index, 1);
        this.handlers.patch({ npcRoster: roster });
        break;
      }
      case 'clear-rivals':
        if (model.isHost && roster.length > 0) this.handlers.patch({ npcRoster: [] });
        break;
      case 'kick':
      case 'ban': {
        const playerId = target.dataset.player ?? '';
        if (!model.isHost || !playerId) return;
        const ban = target.dataset.lobbyAction === 'ban';
        this.confirm(`${target.dataset.lobbyAction}:${playerId}`, () => this.handlers.kick(playerId, ban));
        break;
      }
      case 'open-tracks':
        if (model.isHost) this.setTrackPicker(true);
        break;
      case 'close-tracks':
        this.setTrackPicker(false);
        break;
      case 'pick-track': {
        const mapId = target.dataset.map ?? '';
        this.trackPickerOpen = false;
        if (model.isHost && mapId && mapId !== model.settings.mapId) this.handlers.patch({ mapId });
        else this.setTrackPicker(false);
        break;
      }
      case 'laps': {
        if (!model.isHost) return;
        const value = target.dataset.value;
        if (value === 'default') {
          this.handlers.patch({ laps: null });
          return;
        }
        const current = raceLapCount(model.settings, model.map.laps);
        const next = Math.max(MIN_CUSTOM_LAPS, Math.min(MAX_CUSTOM_LAPS, current + Number(value)));
        if (next !== current) this.handlers.patch({ laps: next });
        break;
      }
      case 'players': {
        if (!model.isHost) return;
        const next = Math.max(
          Math.max(MIN_LOBBY_PLAYERS, model.members.length),
          Math.min(MAX_LOBBY_PLAYERS, model.settings.maxPlayers + Number(target.dataset.value)),
        );
        if (next !== model.settings.maxPlayers) this.handlers.patch({ maxPlayers: next });
        break;
      }
    }
  }

  private handleChange(event: Event): void {
    const model = this.model;
    const input = event.target as HTMLInputElement | HTMLSelectElement;
    if (!model?.isHost || !input.dataset.lobbySetting) return;
    const value = input instanceof HTMLInputElement && input.type === 'checkbox' ? String(input.checked) : input.value;
    this.applySetting(input.dataset.lobbySetting, value);
  }

  private applySetting(setting: string, value: string): void {
    switch (setting) {
      case 'eventMode':
        this.handlers.patch({ eventMode: value as RaceEventMode });
        break;
      case 'timeOfDay':
        this.handlers.patch({ timeOfDay: value === 'map' ? null : value as TimeOfDay });
        break;
      case 'weather':
        this.handlers.patch({ weather: value === 'map' ? null : value as Weather });
        break;
      case 'traffic':
      case 'collisions':
      case 'antiCheat':
        this.handlers.patch({ [setting]: value === 'true' });
        break;
      case 'arcadePreset':
        this.handlers.patch({ arcadePreset: normaliseArcadePreset(value) });
        break;
    }
  }
}

/** Ready/launch state shared by the panel and the host's start shortcut. */
export function lobbyLaunchState(model: LobbyViewModel): {
  waitingFor: LobbyMember[];
  canForceStart: boolean;
  competitors: number;
  blocked: string;
} {
  const mode = raceEventMode(model.settings);
  const competitors = model.members.length + (mode === 'time-trial' ? 0 : model.rivals.length);
  const needed = minimumCompetitorsFor(mode);
  const waitingFor = model.members.filter((member) => member.connected && !member.ready && member.id !== model.localPlayerId);
  const blocked = competitors < needed
    ? t('{mode} needs {n} racers — add a rival', { mode: t(eventModeLabel(mode)), n: needed })
    : model.kind === 'offline' && competitors < 2 && mode !== 'time-trial'
      ? t('Add at least one rival or pick Time Trial')
      : '';
  return {
    waitingFor,
    canForceStart: model.isHost && model.kind === 'online' && model.trackReady && waitingFor.length === 0 && !blocked && model.members.length > 1,
    competitors,
    blocked,
  };
}

function lobbyHtml(model: LobbyViewModel, trackPickerOpen: boolean, confirmKey: string): string {
  const { settings, map, isHost } = model;
  const mode = raceEventMode(settings);
  const briefing = mapBriefing(map);
  const laps = raceLapCount(settings, map.laps);
  const host = model.members.find((member) => member.host);
  const launch = lobbyLaunchState(model);
  const timeOfDay = settings.timeOfDay ?? map.timeOfDay;
  const weather = settings.weather ?? map.weather;
  const visibility = settings.visibility === 'public' ? 'public' : 'private';
  const locked = !isHost;
  const disabled = locked ? 'disabled' : '';
  const title = model.kind === 'offline'
    ? t('OFFLINE SESSION')
    : host ? t("{name}'S LOBBY", { name: host.name }) : t('PRIVATE LOBBY');
  const roster = npcRosterOf(settings);
  const gridCount = model.members.length + (mode === 'time-trial' ? 0 : roster.length);
  const arcade = normaliseArcadePreset(settings.arcadePreset);
  const readyCount = model.members.filter((member) => member.ready && member.connected).length;
  const statusLine = model.notice
    || launch.blocked
    || (model.launching ? t('Building the circuit and warming up the grid…')
      : model.kind === 'online' && !model.trackReady ? t('Loading the track in the background…')
        : model.kind === 'offline' ? t('Ready when you are — the grid releases instantly.')
          : launch.waitingFor.length > 0
            ? t('Waiting for {names}', { names: launch.waitingFor.map((member) => member.name).join(', ') })
            : model.localReady ? t('You are ready — the race starts when everyone is.')
              : model.members.length === 1 ? t('Solo for now — share the invite, or ready up to race the AI.')
                : t('Everyone else is ready. Confirm to launch.'));

  return `
    <div class="lobby-shell" data-kind="${model.kind}" data-host="${isHost}" style="--map-accent:${safeColour(map.palette.accent)};--map-sky:${safeColour(map.palette.skyTop)};--map-horizon:${safeColour(map.palette.skyHorizon)}">
      <header class="lobby-head">
        <div class="lobby-title">
          <p class="eyebrow">${model.kind === 'offline' ? t('STEP 5 · SOLO VS AI') : t('STEP 5 · MULTIPLAYER LOBBY')}</p>
          <h1 id="lobby-title">${escapeHtml(title)}</h1>
          ${model.eventBanner ? `<span class="lobby-event-banner"><small>${escapeHtml(model.eventBanner.kicker)}</small><b>${escapeHtml(model.eventBanner.title)}</b></span>` : ''}
        </div>
        <div class="lobby-head-actions">
          ${model.kind === 'online' ? `
            <button type="button" class="lobby-visibility" data-lobby-action="visibility" data-focus-key="visibility" data-visibility="${visibility}" ${disabled} aria-pressed="${visibility === 'public'}" title="${isHost ? t('Public lobbies appear in everyone’s lobby browser') : t('Only the host can change visibility')}">
              <i aria-hidden="true">${visibility === 'public' ? '◉' : '◎'}</i><span><small>${t('VISIBILITY')}</small><b>${visibility === 'public' ? t('PUBLIC') : t('PRIVATE · LINK ONLY')}</b></span>
            </button>` : ''}
          <button type="button" class="lobby-garage-button" data-lobby-action="garage" data-focus-key="garage" title="${t('Change car and livery (G)')}"><i aria-hidden="true">⚙</i>${t('GARAGE')} <kbd>G</kbd></button>
          <button type="button" class="lobby-leave" data-lobby-action="leave" data-focus-key="leave" data-pad-back data-pad-back-label="${t('LEAVE')}">${t('LEAVE')}</button>
        </div>
      </header>

      <div class="lobby-body">
        <section class="lobby-race" aria-labelledby="lobby-race-heading" data-scroll-key="race">
          <h2 class="lobby-section-title" id="lobby-race-heading"><span>${t('RACE')}</span><small>${isHost ? t('YOU ARE THE HOST') : t('SET BY THE HOST · LIVE')}</small></h2>
          <article class="lobby-track-card">
            <div class="lobby-track-art"><canvas class="map-outline" data-map-outline="${escapeHtml(map.id)}" data-outline-sections="true" aria-hidden="true"></canvas></div>
            <div class="lobby-track-copy">
              <small>${escapeHtml(t(map.location))}</small>
              <h3>${escapeHtml(map.name)}</h3>
              <p>${escapeHtml(t(map.tagline))}</p>
              ${mapTraitBadges(map)}
              <ul class="lobby-track-stats">
                <li><b>${laps}</b><span>${mode === 'heat-run' ? t('HEAT LVLS') : laps === 1 ? t('LAP') : t('LAPS')}</span></li>
                <li><b>${(briefing.lapKilometres * Math.max(1, laps)).toFixed(1)}</b><span>${t('KM')}</span></li>
                <li><b>${briefing.corners}</b><span>${t('CORNERS')}</span></li>
                <li><b>${briefing.shortcuts + briefing.loops}</b><span>${t('ALT ROUTES')}</span></li>
              </ul>
            </div>
            <button type="button" class="lobby-track-change" data-lobby-action="open-tracks" data-focus-key="open-tracks" ${disabled}>${isHost ? t('CHANGE TRACK') : t('TRACK')} <b aria-hidden="true">${isHost ? '▸' : '🔒'}</b></button>
          </article>

          <div class="lobby-field">
            <span class="lobby-field-label">${t('FORMAT')}</span>
            <div class="lobby-format-grid" role="radiogroup" aria-label="${t('Event format')}">
              ${EVENT_FORMATS.map((format) => {
                const active = format.mode === mode;
                return `<button type="button" role="radio" aria-checked="${active}" class="lobby-format ${active ? 'active' : ''}" data-lobby-set="eventMode" data-value="${format.mode}" data-focus-key="format-${format.mode}" ${disabled} title="${escapeHtml(t(format.detail))}"><b>${t(eventModeLabel(format.mode))}</b><small>${escapeHtml(t(format.detail))}</small></button>`;
              }).join('')}
            </div>
          </div>

          <div class="lobby-field-row">
            <div class="lobby-field">
              <span class="lobby-field-label">${mode === 'heat-run' ? t('HEAT LEVELS') : t('LAPS')}</span>
              <div class="lobby-stepper" aria-label="${t('Laps')}">
                <button type="button" data-lobby-action="laps" data-value="-1" data-focus-key="laps-dec" ${disabled || !lapsConfigurable(mode) || laps <= MIN_CUSTOM_LAPS ? 'disabled' : ''} aria-label="${t('One lap fewer')}">−</button>
                <output>${laps}</output>
                <button type="button" data-lobby-action="laps" data-value="1" data-focus-key="laps-inc" ${disabled || !lapsConfigurable(mode) || laps >= MAX_CUSTOM_LAPS ? 'disabled' : ''} aria-label="${t('One more lap')}">+</button>
                ${settings.laps !== undefined && lapsConfigurable(mode) ? `<button type="button" class="lobby-reset-chip" data-lobby-action="laps" data-value="default" data-focus-key="laps-default" ${disabled}>${t('TRACK DEFAULT')}</button>` : `<small>${lapsConfigurable(mode) ? t('TRACK DEFAULT') : t('FIXED BY FORMAT')}</small>`}
              </div>
            </div>
            ${model.kind === 'online' ? `
              <div class="lobby-field">
                <span class="lobby-field-label">${t('DRIVER SLOTS')}</span>
                <div class="lobby-stepper" aria-label="${t('Human driver slots')}">
                  <button type="button" data-lobby-action="players" data-value="-1" data-focus-key="players-dec" ${disabled || settings.maxPlayers <= Math.max(MIN_LOBBY_PLAYERS, model.members.length) ? 'disabled' : ''} aria-label="${t('Fewer driver slots')}">−</button>
                  <output>${settings.maxPlayers}</output>
                  <button type="button" data-lobby-action="players" data-value="1" data-focus-key="players-inc" ${disabled || settings.maxPlayers >= MAX_LOBBY_PLAYERS ? 'disabled' : ''} aria-label="${t('More driver slots')}">+</button>
                  <small>${t('{n} JOINED', { n: model.members.length })}</small>
                </div>
              </div>` : ''}
          </div>

          <div class="lobby-field">
            <span class="lobby-field-label">${t('TIME OF DAY')}</span>
            <div class="lobby-segmented" role="radiogroup" aria-label="${t('Time of day')}">
              ${TIMES.map((time) => `<button type="button" role="radio" aria-checked="${time === timeOfDay}" class="${time === timeOfDay ? 'active' : ''}" data-lobby-set="timeOfDay" data-value="${time}" data-focus-key="time-${time}" ${disabled}><i aria-hidden="true">${TIME_ICON[time]}</i>${t(time.toUpperCase())}${time === map.timeOfDay ? `<small>${t('TRACK')}</small>` : ''}</button>`).join('')}
            </div>
          </div>
          <div class="lobby-field">
            <span class="lobby-field-label">${t('WEATHER')}</span>
            ${map.biome === 'lunar'
              ? `<p class="lobby-airless"><b>${t('NO ATMOSPHERE')}</b>${t('The Moon has no weather — always a black sky and a hard sun.')}</p>`
              : `<div class="lobby-segmented" role="radiogroup" aria-label="${t('Weather')}">
              ${WEATHER_VALUES.map((value) => `<button type="button" role="radio" aria-checked="${value === weather}" class="${value === weather ? 'active' : ''}" data-lobby-set="weather" data-value="${value}" data-focus-key="weather-${value}" ${disabled}><i aria-hidden="true">${WEATHER_ICON[value]}</i>${t(value.toUpperCase())}${value === map.weather ? `<small>${t('TRACK')}</small>` : ''}</button>`).join('')}
            </div>`}
          </div>

          <div class="lobby-toggles">
            ${toggleHtml('traffic', 'ROAD TRAFFIC', 'Light civilian traffic that rivals plan around.', settings.traffic === true, locked || mode === 'time-trial' || arcade === 'traffic-panic')}
            ${toggleHtml('collisions', 'COLLISIONS', 'Off turns every car into a ghost. Walls stay solid.', settings.collisions !== false, locked || mode === 'road-rage')}
            ${model.kind === 'online'
              ? toggleHtml('antiCheat', 'STRICT RESYNC', 'Server position always wins after a reconnect.', settings.antiCheat === true, locked)
              : `<label class="lobby-select"><span><b>${t('ARCADE LAB')}</b><small>${t('Playful physics. Career records are paused.')}</small></span><select data-lobby-setting="arcadePreset" data-focus-key="arcade" ${disabled}>${ARCADE_PRESETS.map((preset) => `<option value="${preset.id}" ${preset.id === arcade ? 'selected' : ''}>${escapeHtml(t(preset.name))} · ${escapeHtml(t(preset.detail))}</option>`).join('')}</select></label>`}
          </div>
        </section>

        <section class="lobby-grid" aria-labelledby="lobby-grid-heading" data-scroll-key="grid">
          <h2 class="lobby-section-title" id="lobby-grid-heading"><span>${t('STARTING GRID')}</span><small>${tn(gridCount, '{n} CAR ON GRID', '{n} CARS ON GRID')}${model.kind === 'online' ? ` · ${t('{ready} / {total} READY', { ready: readyCount, total: model.members.length })}` : ''}</small></h2>
          <ol class="lobby-drivers">
            ${model.members.map((member, index) => memberRowHtml(model, member, index, confirmKey)).join('')}
            ${model.kind === 'online' && model.members.length < settings.maxPlayers
              ? Array.from({ length: Math.min(2, settings.maxPlayers - model.members.length) }, () => `<li class="lobby-driver open-slot"><b class="lobby-grid-number" aria-hidden="true">··</b><i class="lobby-driver-colour" aria-hidden="true"></i><span><b>${t('OPEN SLOT')}</b><small>${model.inviteLink ? t('Share the invite link') : t('Waiting for a driver')}</small></span></li>`).join('')
              : ''}
          </ol>
          <div class="lobby-rivals-head">
            <span>${t('AI RIVALS')} <small>${mode === 'time-trial' ? t('TIME TRIAL IS SOLO') : `${roster.length} / ${MAX_LOBBY_NPCS}`}</small></span>
            ${isHost && mode !== 'time-trial' ? `<span class="lobby-rival-tools">
              <button type="button" data-lobby-action="add-rival" data-focus-key="add-rival" ${roster.length >= MAX_LOBBY_NPCS ? 'disabled' : ''}>${t('+ ADD RIVAL')}</button>
              <button type="button" data-lobby-action="fill-rivals" data-focus-key="fill-rivals" ${roster.length >= MAX_LOBBY_NPCS ? 'disabled' : ''}>${t('FILL GRID')}</button>
              <button type="button" data-lobby-action="clear-rivals" data-focus-key="clear-rivals" ${roster.length === 0 ? 'disabled' : ''}>${tc('action', 'CLEAR')}</button>
            </span>` : ''}
          </div>
          ${mode === 'time-trial'
            ? `<p class="lobby-empty">${t('Time Trial puts you alone on the road with your personal ghost and medal targets.')}</p>`
            : roster.length === 0
              ? `<p class="lobby-empty">${isHost ? t('No AI rivals yet. Add a few to fill the grid — each one can have its own difficulty.') : t('No AI rivals on this grid.')}</p>`
              : `<ol class="lobby-rivals">${model.rivals.map((rival) => rivalRowHtml(model, rival)).join('')}</ol>`}
        </section>
      </div>

      <footer class="lobby-foot">
        ${model.kind === 'online' && model.inviteLink ? `
          <div class="lobby-invite">
            <span><small>${visibility === 'public' ? t('INVITE LINK · ALSO LISTED PUBLICLY') : t('INVITE LINK')}</small><input type="text" readonly value="${escapeHtml(model.inviteLink)}" aria-label="${t('Invite link')}" data-focus-key="invite-field"></span>
            <button type="button" data-lobby-action="copy-invite" data-focus-key="copy-invite">${t('COPY')}</button>
            <button type="button" data-lobby-action="share" data-focus-key="share">${t('QR + SHARE ↗')}</button>
          </div>` : `<div class="lobby-invite lobby-invite-offline"><small>${t('OFFLINE · THIS DEVICE ONLY')}</small><b>${t('No network needed. Progress is saved locally.')}</b></div>`}
        <div class="lobby-launch">
          <p class="lobby-status" role="status" aria-live="polite"><i data-state="${launch.blocked ? 'blocked' : model.localReady ? 'ready' : 'waiting'}"></i>${escapeHtml(statusLine)}</p>
          <div class="lobby-launch-buttons">
            ${launch.canForceStart && !model.localReady ? `<button type="button" class="lobby-force" data-lobby-action="force-start" data-focus-key="force-start" title="${t('Start now — every other driver is ready')}">${t('FORCE START')}</button>` : ''}
            ${launch.canForceStart && model.localReady ? `<button type="button" class="lobby-force" data-lobby-action="force-start" data-focus-key="force-start">${t('START NOW')}</button>` : ''}
            <button type="button" class="lobby-ready ${model.localReady ? 'active' : ''}" data-lobby-action="ready" data-focus-key="ready" data-pad-default data-pad-start data-pad-verb="${model.localReady ? t('CANCEL') : t('READY')}" aria-pressed="${model.localReady}" ${(model.kind === 'online' && !model.trackReady) || launch.blocked || model.launching ? 'disabled' : ''}>
              ${model.launching ? `<span class="button-spinner" aria-hidden="true"></span>${t('LAUNCHING…')}`
                : model.kind === 'online' && !model.trackReady ? `<span class="button-spinner" aria-hidden="true"></span>${t('LOADING TRACK…')}`
                  : model.localReady ? `<b>✓</b> ${t('READY · CANCEL')}`
                    : model.kind === 'offline' ? `${t('START {mode}', { mode: escapeHtml(t(eventModeLabel(mode))) })} <b>→</b>` : `${t('I’M READY')} <b>✓</b>`}
            </button>
          </div>
        </div>
      </footer>

      ${trackPickerOpen ? trackPickerHtml(model) : ''}
    </div>`;
}

function toggleHtml(key: string, label: string, detail: string, checked: boolean, locked: boolean): string {
  return `<label class="lobby-toggle ${locked ? 'locked' : ''}"><input type="checkbox" data-lobby-setting="${key}" data-focus-key="toggle-${key}" ${checked ? 'checked' : ''} ${locked ? 'disabled' : ''}><i aria-hidden="true"></i><span><b>${t(label)}</b><small>${t(detail)}</small></span></label>`;
}

function memberRowHtml(model: LobbyViewModel, member: LobbyMember, index: number, confirmKey: string): string {
  const car = CAR_BY_ID.get(member.carId);
  const you = member.id === model.localPlayerId;
  const state = !member.connected ? 'offline' : member.ready ? 'ready' : 'waiting';
  const status = !member.connected ? t('RECONNECTING') : member.ready ? t('READY') : t('NOT READY');
  const actions = model.isHost && !you && model.kind === 'online'
    ? `<span class="lobby-member-actions">
        <button type="button" class="${confirmKey === `kick:${member.id}` ? 'confirm' : ''}" data-lobby-action="kick" data-player="${escapeHtml(member.id)}" data-focus-key="kick-${escapeHtml(member.id)}" aria-label="${t('Kick {name}', { name: escapeHtml(member.name) })}">${confirmKey === `kick:${member.id}` ? t('CONFIRM KICK') : t('KICK')}</button>
        <button type="button" class="danger ${confirmKey === `ban:${member.id}` ? 'confirm' : ''}" data-lobby-action="ban" data-player="${escapeHtml(member.id)}" data-focus-key="ban-${escapeHtml(member.id)}" aria-label="${t('Ban {name}', { name: escapeHtml(member.name) })}">${confirmKey === `ban:${member.id}` ? t('CONFIRM BAN') : t('BAN')}</button>
      </span>`
    : '';
  return `<li class="lobby-driver ${you ? 'you' : ''}" data-state="${state}">
    <b class="lobby-grid-number">${String(index + 1).padStart(2, '0')}</b>
    <i class="lobby-driver-colour" style="--driver-colour:${safeColour(member.tuning?.paint ?? car?.defaultTuning.paint ?? '#57e9ff')}" aria-hidden="true"></i>
    <span><b>${escapeHtml(member.name)}${you ? ` <em>${t('YOU')}</em>` : ''}${member.host && model.kind === 'online' ? ` <em class="host">${t('HOST')}</em>` : ''}</b><small>${escapeHtml(car?.name ?? member.carId)} · ${escapeHtml(t(car?.className ?? ''))}</small></span>
    ${actions}
    ${model.kind === 'online' ? signalBarsHtml(member.ping, member.connected) : ''}
    ${model.kind === 'online' ? `<strong class="lobby-ready-chip" data-state="${state}">${status}</strong>` : ''}
  </li>`;
}

function rivalRowHtml(model: LobbyViewModel, rival: LobbyRival): string {
  const car = rival.carId ? CAR_BY_ID.get(rival.carId) : null;
  const gridNumber = model.members.length + rival.index + 1;
  return `<li class="lobby-rival" data-difficulty="${rival.difficulty}">
    <b class="lobby-grid-number">${String(gridNumber).padStart(2, '0')}</b>
    <i class="lobby-driver-colour" style="--driver-colour:${safeColour(rival.paint ?? '#8c98a4')}" aria-hidden="true"></i>
    <span><b>${escapeHtml(rival.name)}${rival.featured ? ` <em class="boss">${t('RIVAL BOSS')}</em>` : ''}</b><small>${car ? escapeHtml(car.name) : t('CAR ASSIGNED AT LAUNCH')}${rival.style ? ` · ${t(rival.style.toUpperCase())}` : ''}</small></span>
    <span class="lobby-difficulty" role="radiogroup" aria-label="${t('Difficulty of {name}', { name: escapeHtml(rival.name) })}">
      ${NPC_DIFFICULTY_ORDER.map((difficulty) => `<button type="button" role="radio" aria-checked="${difficulty === rival.difficulty}" class="${difficulty === rival.difficulty ? 'active' : ''}" data-rival-difficulty="${difficulty}" data-index="${rival.index}" data-focus-key="rival-${rival.index}-${difficulty}" data-level="${difficulty}" ${model.isHost ? '' : 'disabled'}>${t(DIFFICULTY_LABEL[difficulty])}</button>`).join('')}
    </span>
    ${model.isHost ? `<button type="button" class="lobby-rival-remove" data-lobby-action="remove-rival" data-index="${rival.index}" data-focus-key="rival-remove-${rival.index}" aria-label="${t('Remove {name}', { name: escapeHtml(rival.name) })}">×</button>` : ''}
  </li>`;
}

/** Circuits added in this release are flagged in the picker. */
const NEW_MAP_IDS = new Set(['emerald-jungle', 'tranquility-base']);

/** Short, scannable gameplay traits shown as chips on track cards. */
function mapTraitBadges(map: MapDefinition): string {
  const badges: Array<{ label: string; kind: string; title: string }> = [];
  if (NEW_MAP_IDS.has(map.id)) badges.push({ label: t('NEW'), kind: 'new', title: t('New circuit') });
  if (typeof map.gravity === 'number' && map.gravity < 0.95) {
    badges.push({ label: t('LOW G · {percent}%', { percent: Math.round(map.gravity * 100) }), kind: 'gravity', title: t('Reduced gravity: longer, floatier jumps') });
  }
  const ramps = map.features.ramps.length;
  if (ramps >= 3) badges.push({ label: t('{n} JUMPS', { n: ramps }), kind: 'jumps', title: t('{n} ramps on every lap', { n: ramps }) });
  if ((map.features.stuntLoops ?? []).length > 0) badges.push({ label: tc('stunt', 'LOOP'), kind: 'loop', title: t('Vertical stunt loop') });
  if (badges.length === 0) return '';
  return `<span class="track-badges">${badges.map((badge) => `<i class="track-badge" data-badge="${badge.kind}" title="${escapeHtml(badge.title)}">${escapeHtml(badge.label)}</i>`).join('')}</span>`;
}

function trackPickerHtml(model: LobbyViewModel): string {
  return `<div class="lobby-track-picker" role="dialog" aria-modal="true" aria-labelledby="lobby-track-picker-title">
    <div class="lobby-track-picker-card">
      <header><div><p class="eyebrow">${t('HOST CONTROL')}</p><h2 id="lobby-track-picker-title">${t('CHOOSE A TRACK')}</h2></div><button type="button" data-lobby-action="close-tracks" data-pad-back data-pad-back-label="${t('CLOSE')}" aria-label="${t('Close track list')}">×</button></header>
      <div class="lobby-track-options">
        ${model.maps.map((map, index) => {
          const active = map.id === model.settings.mapId;
          const briefing = mapBriefing(map);
          return `<button type="button" class="lobby-track-option ${active ? 'active' : ''}" aria-pressed="${active}" data-lobby-action="pick-track" data-map="${escapeHtml(map.id)}" style="--map-accent:${safeColour(map.palette.accent)};--map-sky:${safeColour(map.palette.skyTop)};--map-horizon:${safeColour(map.palette.skyHorizon)}">
            <span class="lobby-track-option-art"><canvas class="map-outline" data-map-outline="${escapeHtml(map.id)}" aria-hidden="true"></canvas><em>${String(index + 1).padStart(2, '0')}</em>${mapTraitBadges(map)}</span>
            <span class="lobby-track-option-copy"><b>${escapeHtml(map.name)}</b><small>${escapeHtml(t(map.location))}</small><small>${t('{laps} LAPS · {km} KM', { laps: map.laps, km: briefing.lapKilometres.toFixed(1) })} · ${t(map.timeOfDay.toUpperCase())} · ${t(map.weather.toUpperCase())}</small></span>
          </button>`;
        }).join('')}
      </div>
    </div>
  </div>`;
}
